import React, { useEffect, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Modal as AntModal } from 'antd';
import produce from 'immer';
import { TableMainText } from 'styles';

import { DEFAULT_PAGE_SIZE } from 'appConstants';
import { TableActions, TableTimestamp } from 'components';
import CustomIcon from 'components/Icons/Icon';

import BDOSettingsCardComponents from '../BDOSettingsCardComponents';
import { clearBDOSettings, deleteBDOSetting, getBDOSettings } from '../action';
import { ICreditCardReducerState } from '../types';
import AddEditVouchersModal from './AddEditVouchersModal';

const { confirm } = AntModal;
const VOUCHER_TYPE = 'evoucher';

const VouchersList = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState<number | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { bdoSettings, loading, error } = useSelector((state: any) => state.bdoSettings);

  const paramsReducer = (state: ICreditCardReducerState, action): ICreditCardReducerState =>
    produce(state, draft => {
      switch (action.type) {
        case 'SEARCH':
          draft.q = action.value;
          draft.page = 1;
          break;
        case 'PAGINATION':
          draft.page = action.current;
          draft.size = action.size ? action.size : draft.size;
          break;
      }
    });

  const columns = [
    {
      title: '',
      key: 'sort',
      width: '3%',
      render: () => <CustomIcon iconName="sort" />
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => (
        <>
          <Link to={`/p/credit_card_redemption_settings/vouchers/show/${record?.id}`}>
            <TableMainText>{record?.name}</TableMainText>
          </Link>
        </>
      )
    },
    {
      title: 'Type',
      dataIndex: 'item_type',
      key: 'item_type',
      render: (_, record) => record?.item_type
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
      render: (_, record) => record?.cost
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (_, record) => record?.category
    },
    {
      title: 'LRS Reward Code',
      dataIndex: 'lrs_reward_code',
      key: 'lrs_reward_code',
      render: (_, record) => record?.lrs_reward_code
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (_, record) => <TableTimestamp timestamp={record['start_date']} />
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (_, record) => <TableTimestamp timestamp={record['end_date']} />
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => (
        <TableActions
          testId="voucher-action-menu"
          actions={[
            {
              title: 'View Details',
              onClick: () => {
                navigate(`/p/credit_card_redemption_settings/vouchers/show/${record?.id}`);
              }
            },
            {
              title: 'Edit',
              onClick: () => {
                setIsEdit(true);
                setSelectedVoucher(record?.id);
                setModalOpen(true);
              }
            },
            {
              title: 'Delete',
              onClick: () =>
                confirm({
                  title: 'Do you want to delete this item?',
                  onOk() {
                    dispatch(deleteBDOSetting(record?.id));
                  }
                })
            }
          ]}
        />
      ),
      align: 'right' as 'right'
    }
  ];

  const [params, paramsDispatch] = useReducer(paramsReducer, {
    q: null,
    size: DEFAULT_PAGE_SIZE,
    page: 1,
    type: VOUCHER_TYPE
  });

  useEffect(() => {
    const paramsToUse = {
      ...params,
      type: VOUCHER_TYPE
    };
    dispatch(getBDOSettings(paramsToUse));
  }, [params, dispatch]);

  const onCloseModal = () => {
    dispatch(clearBDOSettings());
    setIsEdit(false);
    setModalOpen(false);
    setSelectedVoucher(null);
  };

  const displayParams = { ...params, type: VOUCHER_TYPE };

  return (
    <>
      <BDOSettingsCardComponents
        cardTitle="AMEX Voucher Details"
        data={bdoSettings}
        columns={columns}
        mutate={() => dispatch(getBDOSettings(displayParams))}
        params={displayParams}
        paramsDispatch={paramsDispatch}
        loading={loading}
        onAddNewClick={() => {
          dispatch(clearBDOSettings());
          setIsEdit(false);
          setModalOpen(true);
          setSelectedVoucher(null);
        }}
      />
      <AddEditVouchersModal
        modalOpen={modalOpen}
        closeModal={onCloseModal}
        selectedVouchers={selectedVoucher}
        isEdit={isEdit}
      />
    </>
  );
};

export default VouchersList;
